import React, { Component } from 'react';
import { BrowserRouter as Router, Route, Redirect } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import axios from "axios";
import { observable } from 'mobx';
import { observer, inject } from 'mobx-react';
import {reactLocalStorage as Storage} from "reactjs-localstorage";

import 'moment';
import 'moment/locale/ko';
import 'moment-timezone';

import routes from "./routes";
import withTracker from "./withTracker";
import { isSuperAdmin, isSuperAdminOnlyRoute } from "./utils/auth";
import history from "./utils/history";
import { getTargetServerForOperation } from "./utils/targetServerRouting";

import "bootstrap/dist/css/bootstrap.min.css";
import "./assets/shards-dashboards.1.1.0.css";
import "./assets/style.css";


@inject(({ common }) => ({ getSetting: common.getSetting, getIsShowPopup: common.getIsShowPopup, setIsShowPopup: common.setIsShowPopup, isShowModal: common.isShowModal  }))
@inject(({ teamUser }) => ({ authCheck: teamUser.authCheck, getRfAuth: teamUser.getRfAuth, isLogin:teamUser.isLogin, permissions:teamUser.permissions, me:teamUser.me, doLogOut:teamUser.doLogOut }))

@observer
class App extends Component {

  @observable loadingStarted = false;
  constructor(props) {
    super(props);
    if(process.env.NODE_ENV === "development") {
      //axios.defaults.baseURL = 'https://orpapi-base.onreview.co.kr/orv';
      axios.defaults.baseURL = 'https://orpapi.onreview.co.kr/orv';
     axios.defaults.headers.common['X-Target-Server'] = "dev";
    }
    else {
      axios.defaults.baseURL = 'https://orpapi.onreview.co.kr/orv';
    }
    //axios.defaults.baseURL = 'https://ocapi.onreview.co.kr/orv';
    //axios.defaults.baseURL = '/orv';
    axios.defaults.timeout = 120*10000;//10s

    // 특정 오퍼레이션만 별도 X-Target-Server 값으로 분기 (utils/targetServerRouting.js 의 배열로 관리)
    // dev/운영 빌드 모두 적용 — 대상 오퍼레이션에만 헤더를 붙이므로 나머지 요청에는 영향 없음
    axios.interceptors.request.use((config) => {
      // gql() 요청 본문에서 operationName 추출 (직렬화 전 객체 / 후 문자열 모두 대응)
      let op = null;
      const data = config && config.data;
      if (data && typeof data === 'object') op = data.operationName;
      else if (typeof data === 'string') { try { op = JSON.parse(data).operationName; } catch (e) {} }

      const targetServer = getTargetServerForOperation(op);
      if (targetServer !== null && targetServer !== undefined) {
        if (!config.headers) config.headers = {};
        config.headers['X-Target-Server'] = targetServer;
      }
      return config;
    });

    const { getRfAuth } = this.props;

    let noneAuthRoute = ['/', '/signIn', '/signUp', '/cafe24', '/cafeRedirect', '/welcome', '/serviceAgree', '/privateAgree'];
    let cafe24AuthRoute = ['/cafe24', '/cafeRedirect'];

    // 비로그인 마케팅 영역 — `/intro` 와 모든 하위 경로 (정확 매치 + prefix 매치)
    const isPublicPath = (pathname) => {
      if (noneAuthRoute.indexOf(pathname) !== -1) return true;
      if (pathname === '/intro' || pathname.indexOf('/intro/') === 0) return true;
      return false;
    };

    // 토큰 만료 감지 시 강제 로그아웃 + 로그인 페이지로 이동 (중복 호출 방지)
    let _forceLogoutInProgress = false;
    const forceLogout = (reason) => {
      if (_forceLogoutInProgress) return;
      _forceLogoutInProgress = true;
      try {
        Storage.remove('token');
        Storage.remove('rfToken');
      } catch (e) {}
      console.warn('[auth] force logout:', reason);
      // 이미 로그인/공개 페이지면 이동하지 않음
      if (!isPublicPath(history.location.pathname)) {
        window.location.replace('/signIn?logout=1');
      }
    };

    axios.interceptors.response.use(function (res) {
      if(res && res.data && res.data.data && res.data.data.getMe && res.data.data.getMe.data && res.data.data.getMe.data.e) {
        if(res.data.data.getMe.data.e === 'null cafe24Token' || res.data.data.getMe.data.e === 'null cafe24Tokens re') {
          if (!_forceLogoutInProgress) {
            alert("쇼핑몰 관리자 > 앱 > '온리플'앱을 실행하여 로그인 해주세요.");
            forceLogout('null cafe24Token');
          }
        }
      }

      if(noneAuthRoute.indexOf(history.location.pathname) === -1 && res.data && res.data.data && (res.data.data.getMe || res.data.data.getMe === null) ) {
        if(res.data.data.getMe === null || (!res.data.data.getMe.emailId && res.data.data.getMe.emailId !== '')) {
          forceLogout('getMe returned null/empty emailId');
        }
      }

      // GraphQL 응답이 200이지만 errors 배열에 jwt 만료가 포함된 케이스도 처리
      if (res && res.data && res.data.errors && res.data.errors.length > 0) {
        const errMes = res.data.errors[0].message || '';
        if (errMes === 'Context creation failed: jwt expired' && noneAuthRoute.indexOf(history.location.pathname) === -1) {
          getRfAuth(function (ok) {
            if (!ok) forceLogout('refresh token invalid (200/errors)');
          });
        } else if ((errMes === 'Context creation failed: jwt malformed' || errMes === 'Invalid rfAuth.') && noneAuthRoute.indexOf(history.location.pathname) === -1) {
          forceLogout(errMes + ' (200/errors)');
        }
      }

      return res;
    }, function (error) {
      // 네트워크 오류 등으로 response 가 없을 수 있음
      if (!error || !error.response) return Promise.reject(error);

      try { JSON.parse(error.response.config.data); } catch (e) {}

      const errMes = error.response.data
        && error.response.data.errors
        && error.response.data.errors[0]
        && error.response.data.errors[0].message;

      // JWT 만료/무효 — 어떤 operation 이든 동일 처리
      if (errMes === 'Context creation failed: jwt expired') {
        // 공개 페이지에선 무시
        if (noneAuthRoute.indexOf(history.location.pathname) !== -1) return Promise.reject(error);
        // refresh token 으로 재발급 시도
        getRfAuth(function (ok) {
          if (ok) {
            // 새 토큰이 발급되었으니 별도 액션 없음 (다음 요청부터 정상 동작)
          } else {
            // refresh 도 실패 → 강제 로그아웃
            forceLogout('refresh token invalid');
          }
        });
        return Promise.reject(error);
      }

      if (errMes === 'Context creation failed: jwt malformed' || errMes === 'Invalid rfAuth.') {
        forceLogout(errMes);
        return Promise.reject(error);
      }

      return Promise.reject(error);
    });

    if(cafe24AuthRoute.indexOf(history.location.pathname) === -1) {
      if(Storage.get('token')) {
        if(history.location.pathname === '/signIn') {
          this.loadingStarted = true;
        }
        else {
          this.init();
        }
      }
      else {
        this.loadingStarted = true;
        if(!isPublicPath(history.location.pathname)) {
          window.location.replace('signIn');
        }
      }
    }
    else {
      this.loadingStarted = true;
    }

  }


  init = async () => {
    await this.props.authCheck();
    this.loadingStarted = true;
  }

  render() {
    return (
      <HelmetProvider>
        <Router basename={process.env.REACT_APP_BASENAME || "/"}>
          {this.loadingStarted ? (
              <>
                {routes.map((route, index) => {
                  return (
                      <Route
                          key={index}
                          path={route.path}
                          exact={route.exact}
                          history={history}
                          component={withTracker(props => {
                            // 보안: 스토어 관리(aoa)·스토어 모니터/분석(aoaMonitor) 전용 페이지는 슈퍼어드민만 접근.
                            // 일반 사용자가 URL로 직접 접근하면 대시보드로 리다이렉트하여 차단한다.
                            if (isSuperAdminOnlyRoute(route) && !isSuperAdmin(this.props.me)) {
                              return <Redirect to="/dashboard" />;
                            }
                            return (
                                <route.layout {...props} route={route} getIsShowPopup={this.props.getIsShowPopup} setIsShowPopup={this.props.setIsShowPopup} >
                                  <route.component {...props} route={route} />
                                </route.layout>
                            );
                          })}
                      />
                  );
                })}
              </>
          ) : null}

        </Router>
      </HelmetProvider>
    );
  }

}

export default App;
